<template>
    <div class="card" v-show="ready">

        <div class="card-header border-0">
            <div class="row">
                <div class="col-6">
                    <h3 class="mb-0 text-uppercase">{{ cohort.name }}</h3>
                </div>
                <div class="col-6 text-right">
                    <button-radio-group 
                        :options="roleOptions"
                        button-classes="btn-secondary btn-sm"
                        v-model="role"
                        class="d-inline">
                    </button-radio-group>
                </div>
            </div>
        </div>

        <p class="card-body pt-0 text-sm" v-if="empty">{{ $t('cohorts.no-member') }}</p>

        <div v-if="!empty">
            
            <base-alert type="secondary" class="mb-4 mx-3" v-if="role == 'learner' && cohort.parents.length > 0">
                <i class="fas fa-info-circle mr-1"></i>
                {{ $t('cohorts.edit-cohorts-with-meta-cohorts') }}

                <ul class="pl-3 pt-1 mb-0">
                    <li v-for="parent in cohort.parents" :key="parent.id">
                        <a href="#" @click.prevent="openLearners(parent)">
                            <small>{{ parent.name }}</small>
                        </a>
                    </li>
                </ul>
            </base-alert>

            <table-control :search="true" v-model="search"></table-control>

            <el-table class="table-responsive table-flush"
                    :show-header="false"
                    :empty-text="$t('cohorts.no-member-found')"
                    :data="filteredData">

                <el-table-column width="72">
                    <template v-slot="{row}">
                        <span class="avatar rounded-circle" :class="{'disabled': isWithdrawed(row)}">
                            <img :src="row.avatar">
                        </span>
                    </template>
                </el-table-column>

                <el-table-column>
                    <template v-slot="{row}">
                        <div :class="{'disabled': isWithdrawed(row)}">
                            <h4 class="mb-0">
                                {{ row.name }}
                            </h4>
                            <small v-if="row.owner">
                                {{ row.owner.name }}
                            </small>
                            <small v-if="row.entity">
                                / {{ row.entity.name }}
                            </small>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column align="right" class-name="actions" v-if="canSimplyRemoveMember">
                    <template v-slot="{row}">

                        <a href="#" class="table-action text-danger" v-if="canActOnMember(row)"
                                @click.prevent="removeMember(row.id)">
                            <i class="tim-icons icon-simple-remove"></i>
                        </a>

                    </template>
                </el-table-column>

                <el-table-column align="right" class-name="actions" v-if="canRemoveMemberWithAction">
                    <template v-slot="{row}">
                        <actions-dropdown :actions="actions(row)" v-if="canActOnMember(row)"></actions-dropdown>
                    </template>
                </el-table-column>
                
            </el-table>

            <table-pagination :paging="paging"></table-pagination>
        </div>

        <portal to="modals">

            <confirm-modal v-model="modals.confirmForget">
                <p>{{ $t('cohorts.forget-member-confirm') }}</p>
            </confirm-modal>

        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'

    export default {

        props: {

            cohort: {
                type: Object,
                required: true
            },

            ready: {
                type: Boolean,
                required: false
            },
        },

        data() {
            return {
                role: this.$route.params.role ? this.$route.params.role : 'learner',
                modals: new ModalsController(['confirmForget']),
                paging: {
                    currentPage: 1,
                    perPage: 10,
                    total: 0
                },
                search: '',
            }
        },

        created() {
            this.modals.init(this, 'modals')
        },

        computed: {

            canSimplyRemoveMember() {
                return this.cohort.status != 'archived' && (this.role == 'instructor' || this.cohort.has_registrations == 0)
            },

            canRemoveMemberWithAction() {
                return !this.canSimplyRemoveMember && (this.canWithdrawMember || this.canRemoveMemberAndData)
            },

            canWithdrawMember() {
                return this.cohort.status != 'archived' && this.role == 'learner'
            },

            canRemoveMemberAndData() {
                return this.role == 'learner' && this.$auth.hasPermission('admin.delete-personal-data')
            },

            roleOptions() {
                return [
                    { value: 'learner', label: this.$t('common.learners') + ' (' + this.learners.length + ')' },
                    { value: 'instructor', label: this.$t('common.instructors') + ' (' + this.instructors.length + ')' },
                ]
            },

            empty() {
                return !this.cohort.members || (this.cohort.members.length == 0 && !this.search)
            },

            filteredData() {
                // Search filter.
                let members = this.cohort.members.filter(member => !this.search
                    || member.name.toLowerCase().includes(this.search.toLowerCase())
                )
                // Role filter.
                members = members.filter(member => member.pivot.role == this.role)
                // Sorting.
                members = members.sort((a, b) => a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1)
                this.paging.total = members.length
                return members.slice((this.paging.currentPage - 1) * this.paging.perPage, this.paging.currentPage * this.paging.perPage)
            },

            learners() {
                if (this.empty) {
                    return []
                }
                return this.cohort.members.filter(member => member.pivot.role == 'learner')
            },

            instructors() {
                if (this.empty) {
                    return []
                }
                return this.cohort.members.filter(member => member.pivot.role == 'instructor')
            },
        },

        methods: {

            canActOnMember(row) {
                return !this.$auth.user.owner_id || this.$auth.user.owner_id == row.owner_id
            },

            isWithdrawed(row) {
                return !row.pivot.active
            },

            actions(row) {
                let actions = []

                // Remove the learner and keep the data.
                if (this.canWithdrawMember) {
                    if (this.isWithdrawed(row)) {
                        actions.push({
                            label: this.$t('cohorts.bringback-member'),
                            handler: this.bringbackMember,
                            data: row.id
                        })
                    } else {
                        actions.push({
                            label: this.$t('cohorts.withdraw-member'),
                            handler: this.withdrawMember,
                            data: row.id
                        })
                    }
                }

                // Remove the learner and all its data.
                if (this.canRemoveMemberAndData) {
                    actions.push({
                        label: this.$t('cohorts.forget-member'),
                        handler: this.forgetMember,
                        data: row.id
                    })
                }
                return actions
            },

            removeMember(id) {
                axios.delete('/astral/api/front/cohorts/'+this.$route.params.id+'/members/'+id)
                .then(resp => {
                    this.$emit('change');
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            withdrawMember(id) {
                axios.post('/astral/api/front/cohorts/'+this.$route.params.id+'/members/'+id+'/withdraw')
                .then(resp => {
                    this.$emit('change');
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            bringbackMember(id) {
                axios.post('/astral/api/front/cohorts/'+this.$route.params.id+'/members/'+id+'/bringback')
                .then(resp => {
                    this.$emit('change');
                })
                .catch(err => {
                    this.$auth.handleErrorVm(err, this)
                })        
            },

            forgetMember(id) {
                this.modals.open('confirmForget').then(resp => {
                    if (resp.confirmed) {
                        axios.post('/astral/api/front/cohorts/'+this.$route.params.id+'/members/'+id+'/forget')
                        .then(resp => {
                            this.$emit('change');
                        })
                        .catch(err => {
                            this.$auth.handleErrorVm(err, this)
                        })        
                    }
                })
            },

            openLearners(row) {
                this.$router.push({ name: 'admin-meta-cohort', params: { id: row.id, role: 'learner' } })
            },
        }
    }
</script>

<style scoped>

    .disabled, .disabled h4 {
        color: #bbb !important;
    }
    .disabled img {
        opacity: 0.5;
    }
    .card >>> th,
    .card >>> td {
        padding-right: 0;
    }
    .card >>> th.actions,
    .card >>> td.actions {
        padding-right: 1.5rem;
    }
    .card >>> .table-control form {
        width: 100%;
    }
    .card >>> .alert-secondary a {
        color: #5e72e4;
    }
    .card >>> .alert-secondary a:hover {
        color: #233dd2;
    }

</style>
